// Simple password gate — client-side only. Anyone with DevTools can extract
// the password; treat this as a speed bump for casual visitors, not real
// security. For real protection, use Vercel Deployment Protection or
// Cloudflare Access (server-side, password never reaches the browser).
//
// Configure via Vite env var: VITE_SITE_PASSWORD=<password>
// Set it in `.env.local` for dev or in Vercel Project → Environment Variables
// for production. Falls back to "pulsar" if unset (development convenience).

import { useState } from 'react';

const STORAGE_KEY = 'pulsar_unlock_v1';
const EXPECTED = import.meta.env.VITE_SITE_PASSWORD || 'PulsarRocks!';

export default function PasswordGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  if (unlocked) return children;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === EXPECTED) {
      try {
        localStorage.setItem(STORAGE_KEY, '1');
      } catch {
        // localStorage unavailable — accept the unlock for this session only.
      }
      setUnlocked(true);
    } else {
      setError(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-6 py-10">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-white border border-carbon-100 rounded-card p-8 shadow-sm"
      >
        <div className="flex items-center gap-2.5 mb-7">
          <PulsarMark />
          <span className="text-lg font-semibold">Pulsar</span>
        </div>

        <h1 className="h3 text-carbon-800">Economics preview</h1>
        <p className="body-md text-carbon-500 mt-2">Enter the password to continue.</p>

        <label htmlFor="pulsar-pw" className="sr-only">
          Password
        </label>
        <input
          id="pulsar-pw"
          type="password"
          autoFocus
          autoComplete="current-password"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (error) setError(false);
          }}
          placeholder="Password"
          className={`w-full h-11 mt-6 rounded-sw bg-white border px-3 label num-tabular outline-none transition-shadow ${
            error
              ? 'border-rose-400 focus:ring-2 focus:ring-rose-200'
              : 'border-carbon-200 focus:border-carbon-800 focus:ring-2 focus:ring-ice-200'
          }`}
        />
        {error && (
          <p className="text-xs text-rose-700 mt-2" role="alert">
            That's not it.
          </p>
        )}

        <button
          type="submit"
          className="w-full mt-5 h-11 rounded-full bg-carbon-800 text-white label hover:bg-carbon-900 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ice-200"
        >
          Continue
        </button>
      </form>
    </div>
  );
}

function PulsarMark() {
  return (
    <svg width="20" height="24" viewBox="0 0 50 60" fill="currentColor" aria-hidden="true">
      <path d="M0.003 0H30.717C41.365 0 49.997 8.632 49.997 19.28C49.997 29.928 41.365 38.56 30.717 38.56C23.818 38.528 17.114 35.012 17.113 27.136L20.929 27.136H28.652C32.99 27.136 36.507 23.619 36.507 19.28C36.507 14.942 32.99 11.425 28.652 11.425H0.003V0ZM13.254 14.641V27.136V38.56V38.695V59.906H0.003V38.56V27.892C0.003 27.638 0.010 27.386 0.024 27.136C0.416 20.169 6.189 14.641 13.254 14.641Z" />
    </svg>
  );
}
